import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { PageHighlightCards } from "@/components/ui/PageHighlightsCards/PageHighlightCards";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { isVehicleContactConnection } from "@/lib/utils/connections/vehicle-contact-connections";
import { PropertyDamageIcon } from "@/lib/utils/iconUtils";
import { ErrorBoundary } from "@sentry/react";
import { useEffect, useMemo, useRef } from "react";
import MatterVehicles from "../vehicles/MatterVehicles/MatterVehicles";
import { MatterPropertyDamageTable } from "./MatterPropertyDamageTable";

interface MatterPropertyDamageProps {
  matter: Matter;
  searchPropertyDamageId?: string;
}

function MatterPropertyDamageDangerous({
  matter,
  searchPropertyDamageId,
}: MatterPropertyDamageProps) {
  const { hasPagePermission, hasActionPermission } = useUser();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailPropertyDamage,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterPropertyDamage,
    ACTION_PERMISSION_VALUES.create,
  );

  const { openPropertyDamageModal } = useGlobalModal();
  const { handlePropertyDamageSearch } = useHandleSearchParams();
  const processedSearches = useRef<Set<string>>(new Set());

  const clientVehicleTotalLoss = useMemo(() => {
    const clientContactIds = new Set(
      getMatterContactConnections(matter)
        .filter((connection) => connection.label === "Client")
        .map((connection) => connection.contactId),
    );

    if (clientContactIds.size === 0) {
      return 0;
    }

    const clientVehicleIds = new Set(
      matter.vehicles
        .filter((vehicle) =>
          vehicle.connections.some(
            (connection) =>
              isVehicleContactConnection(connection) &&
              clientContactIds.has(connection.targetObjectId),
          ),
        )
        .map((vehicle) => vehicle.id),
    );

    if (clientVehicleIds.size === 0) {
      return 0;
    }

    return matter.vehicles.reduce(
      (acc, vehicle) =>
        clientVehicleIds.has(vehicle.id) ? acc + (vehicle.totalLoss ?? 0) : acc,
      0,
    );
  }, [matter]);

  useEffect(() => {
    if (!searchPropertyDamageId) return;

    const searchKey = `property-damage-${searchPropertyDamageId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handlePropertyDamageSearch(matter, searchPropertyDamageId);
  }, [searchPropertyDamageId, matter, handlePropertyDamageSearch]);

  const pageHighlightCards = useMemo(() => {
    const propertyDamageTotal = matter.propertyDamages.reduce(
      (acc, propertyDamage) => acc + (propertyDamage.originalAmount ?? 0),
      0,
    );

    return [
      {
        title: "Property damages",
        value: matter.propertyDamages.length,
      },
      {
        title: "Total damages",
        value: propertyDamageTotal + clientVehicleTotalLoss,
        isCurrency: true,
      },
    ];
  }, [matter.propertyDamages, clientVehicleTotalLoss]);

  return (
    <div className="flex flex-col gap-y-4 p-6">
      <Text as="h1" variant="title">
        Property damage
      </Text>
      {hasViewPermission && (
        <PageHighlightCards items={pageHighlightCards} variant="summary" />
      )}
      <div className="flex w-full flex-1 grow flex-wrap items-center gap-x-2">
        <div className="flex grow" />
        {hasCreatePermission && (
          <Button
            onClick={() => {
              openPropertyDamageModal({
                matterId: matter.id,
                editing: false,
                initialTab: "details",
              });
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <PropertyDamageIcon className="h-4 w-4" />
            Create property damage
          </Button>
        )}
      </div>

      {!hasViewPermission && (
        <Card className="w-full border-2">
          <CardHeader className="space-y-1">
            <Text variant="subHeading">
              You do not have permission to view property damage.
            </Text>
          </CardHeader>
        </Card>
      )}
      {hasViewPermission && (
        <>
          {matter.propertyDamages.length === 0 && (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1 text-center">
                <Text
                  variant="body"
                  className="text-muted-foreground font-semibold"
                >
                  You haven&apos;t added a property damage yet.
                </Text>
                <Text variant="subHeading">
                  Create your first by clicking &apos;Create property
                  damage&apos;
                </Text>
              </CardHeader>
            </Card>
          )}
          <Card
            className={cn(
              "w-full border-none",
              matter.propertyDamages.length === 0 && "hidden",
            )}
          >
            <CardContent className="p-6">
              <Card className="border-none">
                <CardContent className="bg-accent pt-4">
                  <div className="grid grid-cols-1 gap-4">
                    <MatterPropertyDamageTable matter={matter} />
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
          <MatterVehicles
            matter={matter}
            showHighlights={false}
            contactConnectionLabel="Client"
            includeUnassigned
            showCreateButton={false}
            showTitle={true}
          />
        </>
      )}
    </div>
  );
}

export default function MatterPropertyDamage(props: MatterPropertyDamageProps) {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-6">
          <ServerErrorFallbackCard />
        </div>
      }
    >
      <MatterPropertyDamageDangerous {...props} />
    </ErrorBoundary>
  );
}
